import type { Rules } from "odyssey-shared";
import type { BagSource } from "../bag/store.ts";
import type { ArrangedSnapshot } from "./arranged.ts";
import type { BarSlotSpec } from "./layout.ts";
import { firstStackOf, resolveBar } from "./resolve.ts";

/**
 * Binds the bar's slots to the player's bar and the bag (P2.8-T29, ADR-121 decisions 2.3 and 9): the slots are
 * resolved again each time either changes, and a press on an item slot uses the first bag slot holding the item as the
 * bag stands at the press (ADR-121 decision 9.3), through the bag's own path, so its pending mark, its request budget
 * and its timeout are the bag window's.
 */

/**
 * The bar's store, as far as the binding reads it.
 */
export interface BoundBar {
  /**
   * Sets what every slot holds.
   *
   * @param specs The ten slots.
   */
  setSlots(specs: readonly BarSlotSpec[]): void;
  /**
   * Binds what a use of an item slot does.
   *
   * @param use Uses the first stack of an item, answering whether a request was sent.
   */
  bindUseItem(use: (item: string) => boolean): void;
}

/**
 * The player's bar, as far as the binding reads it.
 */
export interface BoundArranged {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => ArrangedSnapshot;
}

/**
 * Binds the bar to the player's bar and the bag.
 *
 * @param bar The bar's store.
 * @param bag The page's bag, with its `use`.
 * @param rules The page's rules.
 * @param arranged The player's bar, or null where nothing arranges it (the default bar is drawn).
 * @param onSlots Told the slots each time they are resolved: the fight reads which ability each holds.
 * @returns A function that ends the binding.
 */
export function bindPlayerBar(bar: BoundBar, bag: BagSource & { use(slot: number): boolean }, rules: Rules, arranged: BoundArranged | null, onSlots?: (specs: readonly BarSlotSpec[]) => void): () => void {
  const read = (): void => {
    const specs = resolveBar(arranged?.getSnapshot().record ?? null, rules, bag.getSnapshot());
    bar.setSlots(specs);
    onSlots?.(specs);
  };
  bar.bindUseItem((item) => {
    const slot = firstStackOf(bag.getSnapshot(), item);
    return slot !== null && bag.use(slot);
  });
  read();
  const fromBag = bag.subscribe(read);
  const fromBar = arranged?.subscribe(read) ?? null;
  return () => {
    fromBag();
    fromBar?.();
  };
}
